import { ColumnDef } from '@tanstack/react-table'
import { ChainsSummary } from 'models/summaries'
import { Preferences } from 'models/preferences'
import { SortOption } from 'components/Buttons/TableButtons/TableButtonSort'
import { ReactElement } from 'react'
import { BsXCircle } from 'react-icons/bs'
import { PiXCircle } from 'react-icons/pi'
import { DataTableColumnSort } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import {
  sortableFieldOptions as chainsSortableFieldOptions,
  sortableFields as chainsSortableFields,
  useChainsTableColumns,
} from './ChainsTableColumns'
import {
  sortableFieldOptions as chainsReportsSortableFieldOptions,
  sortableFields as chainsReportsSortableFields,
  useChainsTableColumnsV2,
} from './ChainsTableColumnsV2'

/**
 * The axes on which the two restaurant/Chains table variants differ
 * (FIR-7728, step 1 — collapse `ChainsTableComponent` V1 + V2). Everything else
 * — the tri-mode elastic search, the `setChainsSummary` header, the menu modal,
 * the product selector, the location columns/filters, and the full action set —
 * is identical and lives once in the shared `<ChainsTableComponent>`. Both
 * variants read rows + summary from the unified opportunities endpoints
 * (FIR-6997 cutover); only the column set and action slugs differ.
 *
 *   - V1 `restaurants-bars`     → `ChainProxy` columns
 *   - V2 `chains-reports-table` → `ChainProxyReport` columns
 *
 * (Note: `companyTypeSlug` stays `'restaurants-bars'` for BOTH variants — only
 * the `tableTypeSlug` and the column set change.)
 */
export interface ChainsTableVariant {
  /** tableType slug for tags/distributors/GPOs/campaign/export/deal actions. */
  tableTypeSlug: 'restaurants-bars' | 'chains-reports-table'
  /** Per-variant columns hook (V1 keys `ChainProxy`, V2 keys `ChainProxyReport`). */
  useColumns: (
    chainsSummary: ChainsSummary | undefined,
    setMenuModal: any,
    sorting: DataTableColumnSort[] | undefined,
    data: any,
    selectedProducts: number[],
    preferences: Preferences | undefined,
    inCampaigns?: boolean
  ) => ColumnDef<any, any>[]
  sortableFields: readonly string[]
  sortableFieldOptions: readonly SortOption<string>[]
  /**
   * Forward the previous summary (with `isLoading: true`) during a refetch to
   * avoid a flicker (V2). V1 simply skips the update while loading.
   */
  preserveSummaryWhileLoading: boolean
  /** Icon for the "Unassign from Campaign" action. */
  unassignIcon: ReactElement
}

/** V1 — the original `restaurants-bars` chains table. */
export const chainsTableVariant: ChainsTableVariant = {
  tableTypeSlug: 'restaurants-bars',
  useColumns: useChainsTableColumns,
  sortableFields: chainsSortableFields,
  sortableFieldOptions: chainsSortableFieldOptions,
  preserveSummaryWhileLoading: false,
  unassignIcon: <BsXCircle size={20} />,
}

/** V2 — the CRM-reports chains table (the default variant). */
export const chainsReportsTableVariant: ChainsTableVariant = {
  tableTypeSlug: 'chains-reports-table',
  useColumns: useChainsTableColumnsV2,
  sortableFields: chainsReportsSortableFields,
  sortableFieldOptions: chainsReportsSortableFieldOptions,
  preserveSummaryWhileLoading: true,
  unassignIcon: <PiXCircle size={20} />,
}
